import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { EXTERIOR, PARRIWI_GALLERY, MOSMAN, CONTACT } from "@/lib/assets";

const ease = [0.22, 1, 0.36, 1];

export default function Projects() {
  return (
    <div data-testid="projects-page" className="bg-cream">
      {/* Hero */}
      <section className="pt-36 sm:pt-44 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
            className="max-w-3xl"
          >
            <Overline>Projects</Overline>
            <h1 className="font-serif font-light text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-6">
              A curated portfolio of considered homes.
            </h1>
            <p className="mt-7 text-muted-foreground text-lg font-light leading-relaxed max-w-2xl">
              A growing collection of renovation work across Sydney's Lower North Shore — warm,
              natural and beautifully resolved. New projects are added as they come to life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Parriwi Project */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-border pb-6">
              <div>
                <Overline>Mosman, NSW</Overline>
                <h2 className="font-serif font-light text-ink text-4xl sm:text-5xl tracking-tight mt-3">
                  Parriwi Project
                </h2>
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-taupe">Full Renovation · In Focus</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 relative aspect-[16/9] overflow-hidden group">
              <img
                src={EXTERIOR}
                alt="Parriwi Project exterior"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
            </div>
          </Reveal>

          <div className="mt-10 grid lg:grid-cols-12 gap-10">
            <Reveal delay={0.1} className="lg:col-span-5">
              <p className="font-serif text-ink text-2xl sm:text-3xl leading-snug">
                A considered, design-led renovation balancing natural stone, warm timber and soft,
                light-filled living.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="lg:col-span-7">
              <p className="text-muted-foreground font-light leading-relaxed">
                The Parriwi Project brings together natural materials and a calm, contemporary
                sensibility — sandstone, oak and stone selected to feel timeless and grounded.
                Indoor and outdoor living flow seamlessly, framed by greenery and gentle northern light,
                while the upper floor opens to wrap-around glazing and sweeping water views.
              </p>
            </Reveal>
          </div>

          {/* Editorial gallery */}
          <div className="mt-12 grid sm:grid-cols-2 gap-6 lg:gap-8">
            {PARRIWI_GALLERY.map((img, i) => (
              <Reveal key={img.caption} delay={(i % 2) * 0.08}>
                <figure className="group">
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={img.src}
                      alt={`Parriwi Project — ${img.caption}`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-4 flex items-baseline justify-between border-t border-border pt-3">
                    <span className="font-serif text-xl text-ink">{img.caption}</span>
                    <span className="text-xs uppercase tracking-[0.16em] text-taupe text-right max-w-[60%]">{img.note}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Mosman Apartment — coming soon */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-border pb-6">
              <div>
                <Overline>Mosman, NSW</Overline>
                <h2 className="font-serif font-light text-ink text-4xl sm:text-5xl tracking-tight mt-3">
                  Mosman Apartment
                </h2>
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-taupe">Cosmetic Renovation · Before &amp; After</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl text-muted-foreground font-light leading-relaxed">
              A considered cosmetic transformation of a tired apartment — reworking the kitchen and
              bathroom with charcoal joinery, stone surfaces and refined black detailing to create a
              calm, contemporary home that feels far larger than its footprint.
            </p>
          </Reveal>

          <div className="mt-14 space-y-20">
            {MOSMAN.map((room, i) => (
              <Reveal key={room.name} delay={0.05}>
                <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-end">
                  {/* After — hero */}
                  <figure className="lg:col-span-8 group order-1">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={room.after}
                        alt={`Mosman Apartment ${room.name} after renovation`}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      />
                      <span className="absolute top-4 left-4 bg-ink/85 text-cream text-[0.65rem] uppercase tracking-[0.22em] px-3 py-1.5">
                        After
                      </span>
                    </div>
                    <figcaption className="mt-4 flex items-baseline justify-between border-t border-border pt-3">
                      <span className="font-serif text-2xl text-ink">{room.name}</span>
                      <span className="text-xs uppercase tracking-[0.16em] text-taupe text-right max-w-[55%]">{room.note}</span>
                    </figcaption>
                  </figure>

                  {/* Before — secondary, de-emphasised */}
                  <figure className="lg:col-span-4 order-2">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={room.before}
                        alt={`Mosman Apartment ${room.name} before renovation`}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover saturate-[0.85]"
                      />
                      <span className="absolute top-3 left-3 bg-cream/85 text-ink/70 text-[0.6rem] uppercase tracking-[0.22em] px-2.5 py-1">
                        Before
                      </span>
                    </div>
                    <figcaption className="mt-3 border-t border-border pt-2">
                      <span className="text-xs uppercase tracking-[0.16em] text-taupe">The original {room.name.toLowerCase()}</span>
                    </figcaption>
                  </figure>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-cream py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal><Overline className="text-cream/60">Your project next</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.1] mt-6">
              Could your home be the next Schonell project?
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="projects-cta-link"
              className="inline-flex items-center gap-2 mt-9 bg-cream text-ink px-8 py-4 text-xs uppercase tracking-[0.18em] hover:bg-cream/90 transition-colors"
            >
              Start your enquiry <ArrowRight size={15} />
            </Link>
          </Reveal>
          <p className="mt-6 text-cream/60 text-sm font-light">{CONTACT.location}</p>
        </div>
      </section>
    </div>
  );
}
